import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_GG_PUBLISHABLE_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// 創建 Supabase 客戶端,明確禁用 Realtime 功能以提升效能
export const supabase = createClient(supabaseUrl, supabaseKey, {
  realtime: {
    // 專案未使用 Realtime 功能,禁用以減少資源消耗和避免 Preview 環境的連接問題
    params: {
      eventsPerSecond: 0
    }
  },
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  }
});

export interface UserProfile {
  id: string;
  username?: string | null;
  nickname?: string | null;
  email?: string | null;
  oauth_provider?: string | null;
  last_auth_method?: string | null;
  wallet_address?: string | null;
  external_wallet_address?: string | null;
  is_new_user: boolean;
  world_id_sub?: string | null;
  world_id_credential_type?: string | null;
  is_orb_verified?: boolean | null;
  faction_id?: string | null;
  skill_points?: number | null;
  daily_skill_points_amount?: number | null;
  last_skill_points_reset?: string | null;
  total_synthesis_count?: number | null;
  synthesis_milestone_bonus?: number | null;
  global_decay_reduction?: number | null;
  faction_quest_bonus?: number | null;
  last_sugar_claimed_date?: string | null;
  sugar_claim_count?: number | null;
  first_shop_visit?: boolean | null;
  shop_visited_at?: string | null;
  max_energy?: number | null;
  energy?: number | null;
  energy_recovery_rate?: number | null;
  last_energy_recovery?: string | null;
  total_mining_attempts?: number | null;
  mining_capacity_bonus?: number | null;
  mining_speed_bonus?: number | null;
  total_characters_acquired?: number | null;
  created_at: string;
  updated_at: string;
}

type AccountProfile = {
  id: string;
  username: string | null;
  nickname: string | null;
  email: string | null;
  oauth_provider: string | null;
  last_auth_method: string | null;
  wallet_address: string | null;
  external_wallet_address: string | null;
  created_at: string;
  updated_at: string;
};

type GamePlayerProfile = {
  user_id: string;
  is_new_user: boolean;
  world_id_sub: string | null;
  world_id_credential_type: string | null;
  is_orb_verified: boolean | null;
  faction_id: string | null;
  skill_points: number | null;
  daily_skill_points_amount: number | null;
  last_skill_points_reset: string | null;
  total_synthesis_count: number | null;
  synthesis_milestone_bonus: number | null;
  global_decay_reduction: number | null;
  faction_quest_bonus: number | null;
  last_sugar_claimed_date: string | null;
  sugar_claim_count: number | null;
  first_shop_visit: boolean | null;
  shop_visited_at: string | null;
  max_energy: number | null;
  energy: number | null;
  energy_recovery_rate: number | null;
  last_energy_recovery: string | null;
  total_mining_attempts: number | null;
  mining_capacity_bonus: number | null;
  mining_speed_bonus: number | null;
  total_characters_acquired: number | null;
  created_at: string;
  updated_at: string;
};

function getFunctionProfile<T extends object>(data: unknown, key: string): T | null {
  if (!data || typeof data !== 'object') return null;
  const envelope = data as { success?: unknown; profile?: unknown };
  if (envelope.success !== true || !envelope.profile || typeof envelope.profile !== 'object') return null;
  const profile = envelope.profile as Record<string, unknown>;
  if (typeof profile[key] !== 'string') return null;
  return profile as T;
}

// 用戶相關的 API 函數
export const userAPI = {
  // 獲取當前登入用戶的個人資料
  async getProfile(): Promise<UserProfile | null> {
    // 獲取當前登入的用戶
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('❌ [getProfile] Error getting current user:', userError);
      if (
        userError.name === 'AuthSessionMissingError'
        || (userError as { code?: unknown }).code === 'session_not_found'
      ) {
        throw userError;
      }
      return null;
    }
    
    if (!user) {
      console.warn('⚠️ [getProfile] No authenticated user found');
      return null;
    }
    
    const [accountResponse, gameResponse] = await Promise.all([
      supabase.functions.invoke('get-account-profile', { body: {} }),
      supabase.functions.invoke('get-game-player-profile', { body: {} }),
    ]);

    if (accountResponse.error) throw accountResponse.error;
    if (gameResponse.error) throw gameResponse.error;

    const account = getFunctionProfile<AccountProfile>(accountResponse.data, 'id');
    const game = gameResponse.data?.profile
      ? getFunctionProfile<GamePlayerProfile>(gameResponse.data, 'user_id')
      : null;

    if (!account || account.id !== user.id) {
      throw new Error('帳號資料不完整 - 缺少 central account profile');
    }
    if (!game || game.user_id !== user.id) return null;

    return {
      ...game,
      id: account.id,
      username: account.username,
      nickname: account.nickname,
      email: account.email,
      oauth_provider: account.oauth_provider,
      last_auth_method: account.last_auth_method,
      wallet_address: account.wallet_address,
      external_wallet_address: account.external_wallet_address,
    } as UserProfile;
  },

  // 更新當前用戶資料
  async update(updates: Partial<Pick<UserProfile, 'is_new_user'>>): Promise<UserProfile> {
    console.log('🔄 Updating current user profile:', updates);
    
    // 獲取當前登入的用戶
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('❌ Error getting current user:', userError);
      throw userError;
    }
    
    if (!user) {
      throw new Error('未找到登入用戶');
    }
    
    if (typeof updates.is_new_user !== 'boolean') {
      throw new Error('只允許透過 Bubble Game player profile contract 更新 onboarding 狀態');
    }

    const { data, error } = await supabase.functions.invoke('update-game-player-profile', {
      body: { is_new_user: updates.is_new_user },
    });
    if (error) throw error;
    if (!data?.success) throw new Error('更新遊戲個人資料失敗');

    const refreshedProfile = await this.getProfile();
    if (!refreshedProfile) throw new Error('更新後找不到遊戲個人資料');
    console.log('✅ User profile updated through player profile contract');
    return refreshedProfile;
  },

  // 刪除當前用戶記錄
  async delete(): Promise<void> {
    throw new Error('Bubble Game 帳戶刪除必須使用中央 Delete Account contract。');
  },

  // 標記用戶為已完成新手引導
  async markAsExistingUser(): Promise<UserProfile> {
    console.log('🎯 Marking user as existing user...');
    return await this.update({ is_new_user: false });
  }
};
